/**
 * TradingSocketService — the per-consumer façade over the tab's one TradingSocketConnection.
 *
 * Bound transient: every consumer (widget model, MessagesService, hooks) gets its own instance,
 * exactly as before, but all instances share the singleton connection and therefore the one
 * per-tab worker (ADR 0016 increment ② — trading left SharedWorker; the union across consumers
 * that the worker's port registry used to compute lives in the connection's account ledger).
 *
 * Disposal is scoped to the façade: it releases this consumer's account registrations and
 * completes this consumer's order streams without touching the shared connection.
 */
import type { BatchOrderInput, TradingSocketService } from '.';
import type { TradingAccountsSlot } from './connection';
import { TradingSocketError } from './errors';
import type { ReadonlySignal } from '@preact/signals-react';
import type { Order, OrderUpdate, OrderUpdateReport } from '@thinkalpha/trading-ws-client/contracts/order.js';
import { inject, injectable } from 'inversify';
import { Subject, takeUntil } from 'rxjs';
import type { Observable } from 'rxjs';
import type { SendOrderData } from 'src/routes/widgets/TradeProWidget/api';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

@injectable()
export class TradingSocketServiceImpl implements TradingSocketService {
    readonly #stack = new DisposableStack();
    readonly #connection: ReactBindings['TradingSocketConnection'];
    readonly #log: ReactBindings['Logger'];
    readonly #accountsSlot: TradingAccountsSlot;
    readonly #destroyed$ = new Subject<void>();

    constructor(
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('TradingSocketConnection') connection: ReactBindings['TradingSocketConnection'],
    ) {
        this.#log = logger.getSubLogger({ name: 'TradingSocketService' }, { component: 'trading' });
        this.#connection = connection;
        this.#accountsSlot = this.#stack.use(connection.createAccountsSlot());
        this.#stack.defer(() => {
            this.#destroyed$.next();
            this.#destroyed$.complete();
        });
    }

    get isConnected(): ReadonlySignal<boolean> {
        return this.#connection.isConnected;
    }

    get isAuthenticated(): ReadonlySignal<boolean> {
        return this.#connection.isAuthenticated;
    }

    get joinedAccounts(): ReadonlySignal<Set<string>> {
        return this.#connection.joinedAccounts;
    }

    get orderUpdates$(): Observable<OrderUpdateReport & OrderUpdate> {
        return this.#connection.orderUpdates$.pipe(takeUntil(this.#destroyed$));
    }

    get orderSnapshots$(): Observable<Order> {
        return this.#connection.orderSnapshots$.pipe(takeUntil(this.#destroyed$));
    }

    subscribeToAccounts(accountIds: string[]): () => void {
        if (TRACE) {
            this.#log.trace('Subscribe to accounts', { accountIds });
        }
        this.#accountsSlot.set(accountIds);
        return () => {
            if (TRACE) {
                this.#log.trace('Unsubscribe from accounts');
            }
            this.#accountsSlot.clear();
        };
    }

    async createOrder(data: SendOrderData): Promise<Order> {
        this.#assertNotDisposed('order.create');
        return this.#connection.createOrder(data);
    }

    async createBatchOrders(data: BatchOrderInput): Promise<Order[]> {
        this.#assertNotDisposed('order.batch-create');
        return this.#connection.createBatchOrders(data.orders);
    }

    /** Fail fast after disposal: a request from a torn-down consumer should read as its bug, not time out. */
    #assertNotDisposed(operation: string): void {
        if (this.#stack.disposed) {
            throw new TradingSocketError(`TradingSocketService disposed; dropped request: ${operation}`, v4());
        }
    }

    dispose(): void {
        if (this.#stack.disposed) {
            return;
        }
        this.#stack.dispose();
    }

    [Symbol.dispose](): void {
        this.dispose();
    }
}
